// Card expiry (CorporateCard.expiresAt = last day of the printed month). Expired cards and their Apple Pay devices stay in
// the system for past payments, statements and older receipts, but are hidden wherever something new is picked.
import { todayIso } from '../screens/import/format';

export interface ExpiringCard { active: boolean; expiresAt?: string | null }

/** Past its expiry on `day` (default: today). */
export const cardExpired = (c: ExpiringCard, day = todayIso()) => !!c.expiresAt && c.expiresAt.slice(0, 10) < day;
/** Can be picked for something new: active and not expired. */
export const cardUsable = (c: ExpiringCard, day = todayIso()) => c.active && !cardExpired(c, day);
/** Cards for new payments / selects; `keepId` keeps the card a record already uses (shown, even if expired). */
export const usableCards = <T extends ExpiringCard & { id?: string }>(cards: T[], keepId?: string | null, day = todayIso()) =>
  cards.filter((c) => cardUsable(c, day) || (!!keepId && c.id === keepId));
/** Active but expired cards (Firma → Karten „Abgelaufene Karten“). */
export const expiredCards = <T extends ExpiringCard>(cards: T[], day = todayIso()) => cards.filter((c) => c.active && cardExpired(c, day));

/** "2030-03-31" → "03/30", as printed on the card. */
export const expiryLabel = (iso?: string | null) => (iso && /^\d{4}-\d{2}/.test(iso) ? iso.slice(5, 7) + '/' + iso.slice(2, 4) : '');
/** "03/30" (also "03/2030") → "2030-03" for the API; "" → null; anything incomplete/invalid → undefined (not sent yet). */
export function expiryMonth(v: string | null | undefined): string | null | undefined {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const m = /^(0[1-9]|1[0-2])\/(\d{2}|20\d{2})$/.exec(s);
  return m ? `${m[2]!.length === 2 ? '20' + m[2] : m[2]}-${m[1]}` : undefined;
}
/** Typing mask for MM/JJ: digits only, „/“ after the month, at most 4 digits; „3“ → „03/“ is left to the user. */
export function maskExpiry(raw: string, prev = ''): string {
  const d = raw.replace(/\D/g, '').slice(0, 4);
  // Deleting right after the slash removes the slash, not the digit before it.
  if (raw.length < prev.length && prev.endsWith('/') && d.length === 2) return d.slice(0, 1);
  return d.length >= 2 ? d.slice(0, 2) + '/' + d.slice(2) : d;
}

type CardWithDevices = ExpiringCard & { id: string; bank: string; last4: string; ownerId: string | null; instruments?: Array<{ deviceName: string; deviceCardLast4: string; ownerId: string | null; active: boolean }> };
/**
 * Card or Apple Pay device by its last 4 digits — same order as the server (findCardByLast4): a card valid on the receipt
 * date first (an expired card still matches its older receipts), a card number before a device, of two valid cards the one
 * expiring first, otherwise a card usable today. Cards deactivated without an expiry date are ignored.
 */
export function findCardByLast4<T extends CardWithDevices>(cards: T[], l4: string, day?: string | null) {
  const today = todayIso(), on = day && /^\d{4}-\d{2}-\d{2}/.test(day) ? day.slice(0, 10) : today;
  const known = (c: ExpiringCard) => c.active || !!c.expiresAt, validOn = (c: ExpiringCard) => known(c) && !cardExpired(c, on);
  const exp = (c: ExpiringCard) => (c.expiresAt ? c.expiresAt.slice(0, 10) : '9999-12-31');
  const cand = cards.filter(known).flatMap((c) => [
    ...(c.last4 === l4 ? [{ card: c, device: null as null | NonNullable<T['instruments']>[number] }] : []),
    ...(c.instruments || []).filter((i) => i.active && i.deviceCardLast4 === l4).map((i) => ({ card: c, device: i })),
  ]);
  cand.sort((x, y) => {
    const vx = +validOn(x.card), vy = +validOn(y.card);
    if (vx !== vy) return vy - vx;
    const dx = +!x.device, dy = +!y.device;
    if (dx !== dy) return dy - dx;
    if (vx) return exp(x.card).localeCompare(exp(y.card));
    const ux = +cardUsable(x.card, today), uy = +cardUsable(y.card, today);
    return ux !== uy ? uy - ux : exp(y.card).localeCompare(exp(x.card));
  });
  return cand[0] ?? null;
}
